import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { IParceiro } from '@app/modules/parceiro/model';
import { ParceiroService } from '@app/modules/parceiro/services/parceiro.service';
import { of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import {
  CustomValidators,
  EntityService,
  IEntity,
  Pattern,
  TokenService,
  Utils
} from 'viggo-sistemas-core-web';
import {
  validateCnpj,
  validateCpf
} from '../../../../shared/utils/validators_cpf_cnpj';

@Component({
  selector: 'app-fast-parceiro',
  templateUrl: './fast-parceiro.component.html',
  styleUrls: ['./fast-parceiro.component.scss'],
  providers: [ParceiroService]
})
export class FastParceiroComponent<T extends IEntity> implements OnInit {
  form: UntypedFormGroup;

  @Input() entityService: EntityService<T>;

  @Input() entity: IParceiro;

  @Output() onLoadParceiro = new EventEmitter();

  findParceiro = false;

  messages = {
    nome_razao_social: {
      required: 'O nome é requerido.',
      maxlength: 'O nome deve ter no maximo 60 caracteres.',
      minlength: 'O nome é requerido.',
      isEmptyValue: 'Valor inválido: remova os espaços em branco!'
    },
    apelido_nome_fantasia: {
      maxlength: 'Este nome é muito longo.',
      minlength: 'Este nome é muito curto.',
      isEmptyValue: 'Valor inválido: remova os espaços em branco!'
    },
    cpf_cnpj: {
      repeated: 'Informação já cadastrada.',
      mask: 'Valor inválido.',
      required: 'O CPF/CNPJ é obrigatório',
      pattern: 'Este CPF/CNPJ é inválido.',
      maxlength: 'O CPF/CNPJ deve conter no máximo 14 caracteres.',
      duplicate: 'O CPF/CNPJ já esta cadastrado',
      validateCPF: 'Este CPF é inválido.',
      validateCNPJ: 'Este CNPJ é inválido.'
    },
    rg_insc_est: {
      pattern: 'Este valor é inválido.',
      maxlength: 'O campo deve conter no máximo 20 caracteres.',
      duplicate: 'Rg/Insc.Est já existe na base de dados',
      existSpecialChar: 'Valor inválido, não aceita pontuação!',
      isEmptyValue: 'Valor inválido: remova os espaços em branco!'
    }
  };

  constructor(
    protected fb: UntypedFormBuilder,
    protected parceiroService: ParceiroService,
    protected tokenService: TokenService
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      nome_razao_social: [
        this.entity.nome_razao_social,
        [
          Validators.required,
          Validators.maxLength(60),
          Validators.minLength(1),
          CustomValidators.isEmptyValue()
        ]
      ],
      apelido_nome_fantasia: [
        this.entity.apelido_nome_fantasia,
        [
          Validators.minLength(1),
          Validators.maxLength(60),
          CustomValidators.isEmptyValue()
        ]
      ],
      cpf_cnpj: [this.entity.cpf_cnpj, [Validators.required]],
      rg_insc_est: [
        this.entity.rg_insc_est,
        [
          Validators.pattern(Pattern.patternInscEstadual()),
          Validators.maxLength(20),
          CustomValidators.existSpecialChar(),
          CustomValidators.isEmptyValue()
        ]
      ]
    });
    this.form.controls.cpf_cnpj.valueChanges.subscribe(() => {
      this.validar();
    });
  }
  validar() {
    let cpfCnpjValue = this.form.controls.cpf_cnpj.value;
    if (cpfCnpjValue.toString().length <= 11) {
      if (validateCpf(cpfCnpjValue)) {
        this.form.controls.cpf_cnpj.setErrors({ validateCPF: true });
        this.form.updateValueAndValidity();
      }
    } else {
      if (validateCnpj(cpfCnpjValue)) {
        this.form.controls.cpf_cnpj.setErrors({ validateCNPJ: true });
        this.form.updateValueAndValidity();
      }
    }
  }

  notText(event) {
    if (
      (!/^\d+$/.test(event.key) || /[`,.]/.test(event.key)) &&
      event.key !== 'Backspace' &&
      event.key !== 'Delete' &&
      event.key !== 'Tab' &&
      event.key !== 'ArrowLeft' &&
      event.key !== 'ArrowRight' &&
      !event.ctrlKey
    )
      return false;
  }

  public checkRgInscEstadual(): void {
    const rgInsc = this.form.controls.rg_insc_est;

    if (
      rgInsc.value === null ||
      rgInsc.invalid ||
      rgInsc.value === '' ||
      this.findParceiro
    )
      return;
    const rgInscValue: string = rgInsc.value.replace(/[^a-zA-Z0-9 ]/g, '');

    this.parceiroService
      .getEntitiesByParams(
        `rg_insc_est=${rgInscValue}`,
        this.tokenService.getDomain().id
      )
      .subscribe((parceiro: IParceiro) => {
        if (parceiro[0] && parceiro[0].id) {
          this.form.controls.rg_insc_est.setErrors({ duplicate: true });
          this.form.updateValueAndValidity();
          return {} as IParceiro;
        }
      });
  }

  public checkCpfCnpj(): void {
    const cpfCnpj = this.form.controls.cpf_cnpj;

    if (cpfCnpj.invalid) return;
    const cpfCnpjValue: string = cpfCnpj.value;

    this.parceiroService
      .getEntitiesByParams(
        `cpf_cnpj=${cpfCnpjValue}`,
        this.tokenService.getDomain().id
      )
      .pipe(
        map(parceiros => {
          if (parceiros.length) {
            this.findParceiro = true;
            return { ...parceiros[0], cpf_cnpj: parceiros[0].cpf_cnpj.trim() };
          } else {
            this.findParceiro = false;
            return {} as IParceiro;
          }
        }),
        mergeMap(parceiro => {
          if (parceiro === undefined) return of(parceiro);
          return this.entityService.getEntity(parceiro.id).pipe(
            map(ent => {
              if (ent && ent.id) {
                this.form.controls.cpf_cnpj.setErrors({ duplicate: true });
                this.form.updateValueAndValidity();
                return {} as IParceiro;
              }
              return parceiro;
            }),
            catchError(() => of(parceiro))
          );
        })
      )
      .subscribe((parceiro: IParceiro) => {
        if (parceiro === undefined) return;
        else if (Object.keys(parceiro).length !== 0) {
          const { controls } = this.form;
          this.onLoadParceiro.emit(parceiro);
          if (parceiro.apelido_nome_fantasia)
            controls.apelido_nome_fantasia.setValue(
              parceiro.apelido_nome_fantasia
            );
          if (parceiro.nome_razao_social)
            controls.nome_razao_social.setValue(parceiro.nome_razao_social);
          if (parceiro.rg_insc_est)
            controls.rg_insc_est.setValue(parceiro.rg_insc_est);
        }
      });
  }

  getValues(): IParceiro {
    return Utils.clearObject<IParceiro>(this.form.value);
  }
}
